/** The composed runtime's world: plain JSON only, so snapshot = clone,
 * restore = clone, and replay/reconnect/server parity need no per-game code.
 *
 * Geometry convention (every composed entity): `x` is the horizontal CENTRE,
 * `y` is the BOTTOM edge (feet / base), `w`/`h` the collision box. A surface's
 * top is therefore `y - h`. World y grows downward. */
import type { JsonObject } from '../json.js';
import type { AnimStateName } from '../animStates.js';

/** Fixed simulation quantum: 30 Hz. Every timer is an integer tick count. */
export const COMPOSE_TICK_HZ = 30;
export const COMPOSE_DT = 1 / COMPOSE_TICK_HZ;

/** Entity visibility/participation, owned by whichever module parks it.
 * `active` moves and collides; `frozen` is drawn but neither moves nor
 * collides; `hidden` is neither drawn nor simulated (in a pipe, respawning). */
export type EntityStatusV1 = 'active' | 'frozen' | 'hidden';

export interface EntityAnimV1 {
  state: AnimStateName;
  /** Bumps when the state changes or a module re-triggers a one-shot. */
  seq: number;
  /** Platform reaction (`flicker`, `spin`, …) or null. */
  react: string | null;
  reactSeq: number;
}

export interface EntityV1 {
  id: string;
  archetype: string;
  /** Static archetype tags plus dynamic tags modules add/remove
   * (`vulnerable`, `defeated`, …). Kept sorted. */
  tags: string[];
  /** Player seat for player archetypes, else null. */
  slot: number | null;
  status: EntityStatusV1;
  x: number; y: number; w: number; h: number;
  /** Height above the ground plane (world units): flying, falling, lobbed.
   * Presentation lifts the drawable by it (with a contact shadow); gameplay
   * that cares (a falling bomb lands at 0) reads and writes it. Spawned at
   * the archetype's `elevation`. Collision stays on the ground plane. */
  z: number;
  vx: number; vy: number;
  facing: 1 | -1;
  /** Attitude in radians, clockwise on screen (y grows down): 0 is the art's
   * authored upright pose. Written only by a body that rotates (a lander);
   * every other entity stays at 0. Never a left/right flip — that is `facing`. */
  angle: number;
  /** Standing on a surface this tick (written by the entity's body module). */
  grounded: boolean;
  /** Surface entity id under the feet, or null. */
  groundId: string | null;
  /** Render opacity hint (telegraphs, ghosts). */
  alpha: number;
  /** Per-instance recolour: an index into the spec palette (a neutral kind
   * drawn as one of many items), or null for the kind's own colours. */
  tint: number | null;
  /** Presentation overlay the host draws over the sprite — a short text label
   * (a tool's name, an order ticket's lines) and a meter (a patience bar, a
   * timing gauge with its target zone). Written by the module the template
   * gives the overlay to; never read as gameplay state. */
  label: string | null;
  meter: EntityMeterV1 | null;
  anim: EntityAnimV1;
  /** Per-module component data, keyed by module id, owned by that module. */
  c: Record<string, JsonObject>;
}

/** A meter overlay: `value` in [0, 1], an optional target zone [lo, hi]. */
export interface EntityMeterV1 {
  value: number;
  zone: [number, number] | null;
}

/** A seat's command. `p` is the pointer's WORLD point when the seat aims
 * with a pointer this tick (the `pointer` input profile: a mouse that moved,
 * a finger or a button that is down), else null (keys, sticks, a resting
 * mouse). `a` is the primary button — a pointer press arrives as `a`. */
export interface PlayerInputV1 {
  dx: -1 | 0 | 1;
  dy: -1 | 0 | 1;
  a: boolean;
  b: boolean;
  p: { x: number; y: number } | null;
}

export interface SeatInputV1 extends PlayerInputV1 {
  /** Held state on the previous tick (for edge detection). */
  prevA: boolean;
  prevB: boolean;
}

/** One intra-tick message between modules. Cleared at the end of each tick;
 * never rendered. */
export interface SignalV1 {
  kind: string;
  /** Entity the signal is about (target), when there is one. */
  target: string | null;
  /** Entity that caused it, when there is one. */
  source: string | null;
  data: JsonObject;
}

/** One presentation event (drained once per frame by the host). */
export interface ComposedEventV1 {
  kind: string;
  tick: number;
  data: JsonObject;
}

export interface WorldV1 {
  schema: 'compose-world@1';
  tick: number;
  /** mulberry32 state (uint32). */
  rng: number;
  nextId: number;
  playerCount: number;
  bounds: { w: number; h: number; wrapX: boolean };
  entities: Record<string, EntityV1>;
  /** Module-owned singletons, keyed by module instance key. */
  globals: Record<string, JsonObject>;
  /** Shared named scalars (difficulty curves write, behaviours read). */
  scalars: Record<string, number>;
  inputs: SeatInputV1[];
  signals: SignalV1[];
  events: ComposedEventV1[];
  scores: number[];
  phase: 'active' | 'won' | 'lost';
  endReason: string | null;
  /** A versus match's winning seat; null for a draw and for every solo or
   * co-op ending (the team wins or loses together). */
  winner: number | null;
}

/** Deterministic id: lexicographic order == creation order. */
export function entityId(n: number): string {
  return 'e' + String(n).padStart(6, '0');
}

export function sortedEntityIds(world: WorldV1): string[] {
  return Object.keys(world.entities).sort();
}

export function hasTag(entity: EntityV1, tag: string): boolean {
  return entity.tags.includes(tag);
}

export function addTag(entity: EntityV1, tag: string): void {
  if (!entity.tags.includes(tag)) { entity.tags.push(tag); entity.tags.sort(); }
}

export function removeTag(entity: EntityV1, tag: string): void {
  const index = entity.tags.indexOf(tag);
  if (index >= 0) entity.tags.splice(index, 1);
}

/** Shortest signed horizontal delta from `from` to `to`, honouring wrap. */
export function deltaX(world: WorldV1, from: number, to: number): number {
  let d = to - from;
  if (world.bounds.wrapX) {
    const w = world.bounds.w;
    if (d > w / 2) d -= w;
    else if (d < -w / 2) d += w;
  }
  return d;
}

/** Deep copy of the world: plain JSON, a tree (no shared or cyclic nodes), so
 * a direct recursive copy equals `structuredClone` and is several times
 * faster — the solver copies the world once per rollout candidate. An own
 * `__proto__` key stays an own data property, as `structuredClone` keeps it. */
export function cloneWorldTree<T>(value: T): T {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    const out = new Array<unknown>(value.length);
    for (let i = 0; i < value.length; i++) out[i] = cloneWorldTree(value[i]);
    return out as T;
  }
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(value)) {
    const v = cloneWorldTree((value as Record<string, unknown>)[key]);
    if (key === '__proto__') Object.defineProperty(out, key, { value: v, enumerable: true, writable: true, configurable: true });
    else out[key] = v;
  }
  return out as T;
}

const HASH_F64 = new Float64Array(1);
const HASH_U32 = new Uint32Array(HASH_F64.buffer);

const mixHash = (h: number, v: number): number => Math.imul(h ^ v, 0x01000193);

function mixString(h: number, s: string): number {
  h = mixHash(h, s.length);
  for (let i = 0; i < s.length; i++) h = mixHash(h, s.charCodeAt(i));
  return h;
}

function hashTree(h: number, value: unknown): number {
  if (value === null) return mixHash(h, 1);
  switch (typeof value) {
    case 'boolean': return mixHash(h, value ? 2 : 3);
    case 'string': return mixString(mixHash(h, 4), value);
    case 'number':
      // Every NaN is one value to Object.is, whatever its bits.
      if (value !== value) return mixHash(h, 9);
      HASH_F64[0] = value;
      return mixHash(mixHash(mixHash(h, 5), HASH_U32[0]!), HASH_U32[1]!);
    case 'object': break;
    default: return mixHash(h, 6);
  }
  if (Array.isArray(value)) {
    h = mixHash(mixHash(h, 7), value.length);
    for (let i = 0; i < value.length; i++) h = hashTree(h, value[i]);
    return h;
  }
  h = mixHash(h, 8);
  for (const key in value as Record<string, unknown>) h = hashTree(mixString(h, key), (value as Record<string, unknown>)[key]);
  return h;
}

/** A 32-bit hash of a world tree that {@link sameWorldTree} trees share
 * (keys in order, numbers by their bits): a bucket key for exact matches,
 * never a substitute for comparing them. */
export function worldTreeHash(value: unknown): number {
  return hashTree(0x811c9dc5, value) >>> 0;
}

/** Exact equality of two world trees: same keys in the same order, same
 * array lengths, numbers by `Object.is` (so -0 differs from 0, which
 * `JSON.stringify` merges). */
export function sameWorldTree(a: unknown, b: unknown): boolean {
  if (a === null || typeof a !== 'object' || b === null || typeof b !== 'object') return Object.is(a, b);
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (!sameWorldTree(a[i], b[i])) return false;
    return true;
  }
  if (Array.isArray(b)) return false;
  const ak = Object.keys(a), bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  for (let i = 0; i < ak.length; i++) {
    if (ak[i] !== bk[i] || !sameWorldTree((a as Record<string, unknown>)[ak[i]!], (b as Record<string, unknown>)[ak[i]!])) return false;
  }
  return true;
}

/** Box overlap (touching counts) using bottom-anchored boxes and wrap. */
export function overlaps(world: WorldV1, a: EntityV1, b: EntityV1, shrink = 0): boolean {
  const dx = Math.abs(deltaX(world, a.x, b.x));
  const ay = a.y - a.h / 2, by = b.y - b.h / 2;
  return dx * 2 <= a.w + b.w - shrink * 2 && Math.abs(ay - by) * 2 <= a.h + b.h - shrink * 2;
}
